import { type ReactNode, useMemo } from 'react'
import { useConnectFeature, useTerrosMcpFeature } from '@hooks'
import { type FeatureState, translateLabels, useTranslation } from '@front/model'
import { type PageRoute } from '../navigation'
import { MilestonePill } from '../MilestonePill'
import { terrosMcpNavRoute } from './terrosMcpRoute'

type IntegrationRouteFlags = FeatureState & {
  isTerrosMcpEnabled?: boolean
  isTerrosMcpLoading?: boolean
}

const connectPill = (milestone: FeatureState['milestone']): ReactNode => (
  <MilestonePill milestone={milestone} showArticle={false} />
)

export const buildIntegrationRoutes = ({
  isEnabled,
  isLoading,
  milestone,
  isTerrosMcpEnabled = false,
  isTerrosMcpLoading = false,
}: IntegrationRouteFlags): PageRoute[] => {
  const connectHidden = !isEnabled || isLoading
  return [
    {
      path: '/integration/marketplace',
      label: 'Marketplace',
      icon: 'storefront',
      scope: 'install:manage',
      hidden: connectHidden,
      rightSection: connectPill(milestone),
    },
    {
      path: '/integration/developer',
      label: 'Developer',
      icon: 'build',
      scope: 'app:manage',
      hidden: connectHidden,
      rightSection: connectPill(milestone),
    },
    {
      path: '/integration/import',
      label: 'Import',
      icon: 'file_save',
      scope: 'admin',
    },
    {
      path: '/integration/export',
      label: 'Export',
      icon: 'file_export',
      scope: 'export:manage',
    },
    {
      path: '/integration/apikey',
      label: 'API Keys',
      icon: 'key',
      scope: 'apikey:manage',
    },
    {
      path: '/integration/webhook',
      label: 'Webhooks',
      icon: 'webhook',
      scope: 'webhook:manage',
    },
    {
      path: '/integration/cli',
      label: 'CLI',
      icon: 'terminal',
    },
    terrosMcpNavRoute({ isEnabled: isTerrosMcpEnabled, isLoading: isTerrosMcpLoading }),
    {
      path: 'https://docs.terros.com/docs/info/overview',
      label: 'Documentation',
      icon: 'open_in_new',
      isExternalLink: true,
    },
  ]
}

export function useIntegrationRoutes(): PageRoute[] {
  const { t } = useTranslation()
  const { isEnabled, isLoading, milestone } = useConnectFeature()
  const { isEnabled: isTerrosMcpEnabled, isLoading: isTerrosMcpLoading } = useTerrosMcpFeature()
  const routes = useMemo(
    () =>
      buildIntegrationRoutes({
        isEnabled,
        isLoading,
        milestone,
        isTerrosMcpEnabled,
        isTerrosMcpLoading,
      }),
    [isEnabled, isLoading, milestone, isTerrosMcpEnabled, isTerrosMcpLoading]
  )
  return translateLabels(routes, t)
}
